import { supabase } from "../supabase";
import { safeUserErrorMessage } from "./safeErrors";
import { normalizeEstimateTaxLinesForSave } from "./estimateTax";
import { isAdmin } from "./permissions";
import {
  createDraftEstimate,
  generateEstimateNumber as bootstrapGenerateEstimateNumber,
  ESTIMATE_BOOTSTRAP_STAGE,
} from "@nexora-systems-inc/estimate-bootstrap";
import {
  applyOrganizationScopeForActor,
  orgScopedInsert,
  resolveOrganizationIdForQueries,
  stampOrganizationId,
} from "./orgQueryScope";
import {
  applyCatalogVisibilityScope,
  stampPlatformCatalogScope,
} from "./catalogOwnership";
import {
  requireEstimateApprove,
  requireEstimateConvert,
  requireEstimateEdit,
  requireEstimateView,
} from "./financialGuards";
import {
  assertClientOperationallyWritable,
  OPERATIONAL_LOCK_CLIENT_MESSAGE,
} from "./operationalLifecycle";
import { isClientArchived } from "./clientVisibility";
import { buildMeasurementWritePayload } from "./measurementModel";

export {
  DEDUCTION_TYPE_OPTIONS,
  RECORD_KIND_OPTIONS,
  SURFACE_TYPE_OPTIONS,
  formatSurfaceTotals,
  formatSurfaceType,
} from "./measurementModel";

async function scopeEstimatesQuery(query, actor) {
  return applyOrganizationScopeForActor(query, actor, supabase, {
    tableName: "estimates",
  });
}

async function scopeClientsQuery(query, actor) {
  return applyOrganizationScopeForActor(query, actor, supabase, {
    tableName: "clients",
  });
}

async function scopeMaterialsQuery(query, actor = null) {
  return applyOrganizationScopeForActor(query, actor, supabase, {
    tableName: "materials",
  });
}

async function scopeFeatureTypesQuery(query, actor = null) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  return applyCatalogVisibilityScope(query, organizationId);
}

async function scopeAssemblyTemplatesQuery(query, actor = null) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  return applyCatalogVisibilityScope(query, organizationId);
}

export function serializeSupabaseError(error) {
  if (!error) return null;
  if (typeof error === "string") return { message: error };
  return {
    code: error.code ?? null,
    message: error.message ?? String(error),
    details: error.details ?? null,
    hint: error.hint ?? null,
  };
}

export function formatSupabaseError(error) {
  if (process.env.NODE_ENV === "production") {
    return safeUserErrorMessage(error);
  }

  const serialized = serializeSupabaseError(error);
  if (!serialized) return "Unknown error";

  return [
    serialized.message,
    serialized.details,
    serialized.hint,
    serialized.code ? `[${serialized.code}]` : null,
  ]
    .filter(Boolean)
    .join(" — ");
}

const MVP_CATALOG = [
  {
    code: "shower",
    name: "Shower",
    category: "plumbing",
    sort_order: 1,
    assemblies: [
      {
        code: "kerdi-shower-5x3",
        name: "5x3 Kerdi Shower",
        assembly_kind: "fixed",
        sort_order: 1,
      },
      {
        code: "kerdi-shower-4x4",
        name: "4x4 Kerdi Shower",
        assembly_kind: "fixed",
        sort_order: 2,
      },
      {
        code: "curbless-shower",
        name: "Curbless Shower",
        assembly_kind: "fixed",
        sort_order: 3,
      },
      {
        code: "custom-shower",
        name: "Custom Shower",
        assembly_kind: "parametric",
        sort_order: 4,
      },
    ],
  },
  {
    code: "floor-tile",
    name: "Floor Tile",
    category: "tile",
    sort_order: 2,
    assemblies: [
      {
        code: "standard-floor-tile",
        name: "Standard Floor Tile",
        assembly_kind: "fixed",
        sort_order: 1,
      },
    ],
  },
  {
    code: "backsplash",
    name: "Backsplash",
    category: "tile",
    sort_order: 3,
    assemblies: [
      {
        code: "standard-backsplash",
        name: "Standard Backsplash",
        assembly_kind: "fixed",
        sort_order: 1,
      },
    ],
  },
  {
    code: "heated-floor",
    name: "Heated Floor",
    category: "electrical",
    sort_order: 4,
    assemblies: [
      {
        code: "standard-heated-floor",
        name: "Standard Heated Floor",
        assembly_kind: "fixed",
        sort_order: 1,
      },
    ],
  },
  {
    code: "accent-wall",
    name: "Accent Wall",
    category: "finish",
    sort_order: 5,
    assemblies: [
      {
        code: "standard-accent-wall",
        name: "Standard Accent Wall",
        assembly_kind: "fixed",
        sort_order: 1,
      },
    ],
  },
];

export function generateEstimateNumber() {
  return bootstrapGenerateEstimateNumber();
}

export const ESTIMATE_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "ready", label: "Ready" },
  { value: "sent", label: "Sent" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
  { value: "converted", label: "Converted" },
  { value: "archived", label: "Archived" },
];

/** Active lifecycle statuses shown under the Estimate Hub "All" filter. */
export const ESTIMATE_ACTIVE_HUB_STATUSES = Object.freeze([
  "draft",
  "ready",
  "sent",
  "approved",
  "declined",
  "converted",
]);

const ESTIMATE_SYSTEM_MANAGED_STATUSES = new Set(["converted", "archived"]);

/** Status values users may pick in UI — excludes system-managed statuses. */
export const ESTIMATE_USER_EDITABLE_STATUS_OPTIONS =
  ESTIMATE_STATUS_OPTIONS.filter(
    (option) => !ESTIMATE_SYSTEM_MANAGED_STATUSES.has(option.value),
  );

/**
 * Canonical, persisted status values (application vocabulary).
 *
 * IMPORTANT: as of this writing the ORIGINAL `estimates` table CHECK
 * constraint (supabase/migrations/20260619120000_create_estimate_engine_phase1b.sql)
 * only allows draft/submitted/approved/converted/rejected/superseded — it does
 * NOT match this list. A later migration
 * (supabase/migrations/20260628120000_estimate_status_vocabulary.sql) already
 * reconciles the `estimates.status` constraint to this exact vocabulary, and
 * supabase/migrations/20260928120000_estimate_status_vocabulary_v2_reconciliation.sql
 * (added alongside this comment) additionally reconciles `estimate_events`.
 * Whether 20260628120000 has actually been applied to the live database could
 * not be confirmed from this repo alone — see that migration's application
 * status before assuming Sent/Declined/Archived work end-to-end.
 */
export const ESTIMATE_STATUS_VALUES = Object.freeze(
  ESTIMATE_STATUS_OPTIONS.map((option) => option.value),
);

export function isValidEstimateStatus(status) {
  return ESTIMATE_STATUS_VALUES.includes(status);
}

export function resolveEstimateStatus(status) {
  return isValidEstimateStatus(status) ? status : "draft";
}

export function formatEstimateStatus(status) {
  const resolved = resolveEstimateStatus(status);
  const match = ESTIMATE_STATUS_OPTIONS.find(
    (option) => option.value === resolved,
  );
  return match?.label || resolved;
}

export function formatEstimateUpdatedAt(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Idempotent MVP catalog bootstrap (organization_id IS NULL).
 *
 * Duplicates were caused by:
 * 1. UNIQUE (organization_id, code) not enforcing uniqueness when organization_id IS NULL.
 * 2. maybeSingle() returning an error (PGRST116) when duplicates existed; only `data`
 *    was checked, so every load inserted again.
 *
 * Fix: scope lookup to null org, use limit(1) + explicit error handling, and rely on
 * partial unique indexes from migration 20260621120000_* as a DB backstop.
 */
export async function ensureMvpEstimateCatalog() {
  for (const feature of MVP_CATALOG) {
    const { data: existingRows, error: lookupError } = await supabase
      .from("feature_types")
      .select("feature_type_id")
      .eq("code", feature.code)
      .eq("catalog_scope", "platform")
      .is("organization_id", null)
      .order("created_at", { ascending: true })
      .limit(1);

    if (lookupError) throw lookupError;

    let featureTypeId = existingRows?.[0]?.feature_type_id;

    if (!featureTypeId) {
      const { data: created, error } = await supabase
        .from("feature_types")
        .insert(
          stampPlatformCatalogScope({
            code: feature.code,
            name: feature.name,
            category: feature.category,
            sort_order: feature.sort_order,
          }),
        )
        .select("feature_type_id")
        .single();

      if (error) {
        // Concurrent bootstrap or unique-index race — re-fetch canonical row.
        if (error.code === "23505") {
          const { data: retryRows, error: retryError } = await supabase
            .from("feature_types")
            .select("feature_type_id")
            .eq("code", feature.code)
            .eq("catalog_scope", "platform")
            .is("organization_id", null)
            .order("created_at", { ascending: true })
            .limit(1);
          if (retryError) throw retryError;
          featureTypeId = retryRows?.[0]?.feature_type_id;
        } else {
          throw error;
        }
      } else {
        featureTypeId = created.feature_type_id;
      }
    }

    if (!featureTypeId) {
      throw new Error(`Failed to resolve feature_type for code "${feature.code}"`);
    }

    for (const assembly of feature.assemblies) {
      const { data: existingAssemblyRows, error: assemblyLookupError } =
        await supabase
          .from("assembly_templates")
          .select("assembly_template_id")
          .eq("code", assembly.code)
          .eq("catalog_scope", "platform")
          .is("organization_id", null)
          .order("created_at", { ascending: true })
          .limit(1);

      if (assemblyLookupError) throw assemblyLookupError;
      if (existingAssemblyRows?.length) continue;

      const { error } = await supabase.from("assembly_templates").insert(
        stampPlatformCatalogScope({
          feature_type_id: featureTypeId,
          code: assembly.code,
          name: assembly.name,
          assembly_kind: assembly.assembly_kind,
          sort_order: assembly.sort_order,
        }),
      );

      if (error && error.code !== "23505") throw error;
    }
  }
}

export async function loadEstimates(actor) {
  requireEstimateView(actor);

  let query = supabase
    .from("estimates")
    .select("*, clients(client_name)")
    .order("updated_at", { ascending: false });

  query = await scopeEstimatesQuery(query, actor);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function loadClientsForEstimates(actor) {
  requireEstimateView(actor);

  let query = supabase
    .from("clients")
    .select("client_id, client_name")
    .is("archived_at", null)
    .eq("client_status", "active")
    .order("client_name");

  if (!isAdmin(actor)) {
    query = query.eq("created_by", actor?.supabase_user_id);
  }

  // Awaiting async scope helpers executes the builder (Postgrest Thenable); chain filters first.
  query = await scopeClientsQuery(query, actor);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function loadFeatureTypesCatalog(actor = null) {
  let query = supabase
    .from("feature_types")
    .select("*")
    .eq("is_active", true)
    .order("sort_order");

  query = await scopeFeatureTypesQuery(query, actor);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export const CATALOG_OTHER_PRODUCT = "__other_product__";
export const CATALOG_OTHER_SERVICE = "__other_service__";
export const CATALOG_OTHER_WORK_TYPE = "__other_work_type__";

export const CATALOG_MATERIAL_TYPE = {
  PRODUCT: "Product",
  SERVICE: "Service",
};

export const LABOUR_PRICING_METHOD = {
  HOURLY: "hourly",
  SQUARE_FOOT: "square_foot",
};

export async function loadMaterialsCatalog(actor = null) {
  let query = supabase
    .from("materials")
    .select(
      "material_id, material_name, sku, unit_of_measure, unit_cost, sell_price, material_type, active",
    )
    .eq("active", true)
    .order("material_name");

  query = await scopeMaterialsQuery(query, actor);

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

// materials.material_type is authoritatively 'Product' | 'Service' as of
// migration 20260627120000_estimate_catalog_product_service.sql. The actual
// root cause of catalog rows disappearing from Add Material/Add Labour
// search was upstream of this function entirely — loadMaterialsCatalog()
// was being called with no actor, so organization scoping fail-closed to
// zero rows before type-filtering ever ran (see EstimateHubScreen.js). A
// prior pass added speculative tolerance here for blank/legacy
// material_type values on the mistaken assumption the type comparison was
// the failure point; that tolerance had no supporting evidence (the real
// catalog rows are correctly typed) and risked silently miscategorizing a
// genuinely incomplete row as a Product, so it has been removed. Only a
// case-insensitive comparison is kept, since that carries no such risk.
export function filterCatalogByMaterialType(catalog, materialType) {
  const target = String(materialType || "").trim().toLowerCase();

  return (catalog || []).filter(
    (item) => String(item.material_type || "").trim().toLowerCase() === target,
  );
}

export async function loadAssemblyTemplatesForFeatureType(featureTypeId, actor = null) {
  if (!featureTypeId) return [];

  let query = supabase
    .from("assembly_templates")
    .select("*")
    .eq("feature_type_id", featureTypeId)
    .eq("is_active", true)
    .order("sort_order");

  query = await scopeAssemblyTemplatesQuery(query, actor);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function loadEstimateTree(actor, estimateId) {
  requireEstimateView(actor);

  let estimateQuery = supabase
    .from("estimates")
    .select("*, clients(client_name)")
    .eq("estimate_id", estimateId)
    .single();

  // Postgrest builders are Thenable; chain .single() before async scope await.
  estimateQuery = await scopeEstimatesQuery(estimateQuery, actor);

  const { data: estimate, error: estimateError } = await estimateQuery;

  if (estimateError) {
    const wrapped = new Error(formatSupabaseError(estimateError));
    wrapped.cause = estimateError;
    throw wrapped;
  }

  const { data: rooms, error: roomsError } = await supabase
    .from("estimate_rooms")
    .select("*")
    .eq("estimate_id", estimateId)
    .eq("is_active", true)
    .order("sort_order");

  if (roomsError) {
    const wrapped = new Error(formatSupabaseError(roomsError));
    wrapped.cause = roomsError;
    throw wrapped;
  }

  const roomIds = (rooms || []).map((room) => room.room_id);
  let features = [];
  let sections = [];
  let workTypeMaterials = [];
  let workTypeLabour = [];

  if (roomIds.length) {
    const { data: featureRows, error: featuresError } = await supabase
      .from("estimate_features")
      .select(
        "*, feature_types(name, code), assembly_templates(name, assembly_kind)",
      )
      .in("room_id", roomIds)
      .eq("is_active", true)
      .order("sort_order");

    if (featuresError) {
      const wrapped = new Error(formatSupabaseError(featuresError));
      wrapped.cause = featuresError;
      throw wrapped;
    }
    features = featureRows || [];

    const featureIds = features.map((f) => f.estimate_feature_id);

    if (featureIds.length) {
      const { data: sectionRows, error: sectionsError } = await supabase
        .from("estimate_room_sections")
        .select("*")
        .in("estimate_feature_id", featureIds)
        .eq("is_active", true)
        .order("sort_order");

      if (sectionsError) {
        const wrapped = new Error(formatSupabaseError(sectionsError));
        wrapped.cause = sectionsError;
        throw wrapped;
      }
      sections = sectionRows || [];

      const { data: materialRows, error: materialsError } = await supabase
        .from("estimate_room_materials")
        .select("*")
        .in("estimate_feature_id", featureIds)
        .eq("is_active", true)
        .order("sort_order");

      if (materialsError) {
        const wrapped = new Error(formatSupabaseError(materialsError));
        wrapped.cause = materialsError;
        throw wrapped;
      }
      workTypeMaterials = materialRows || [];

      const { data: labourRows, error: labourError } = await supabase
        .from("estimate_room_labour")
        .select("*")
        .in("estimate_feature_id", featureIds)
        .order("created_at");

      if (labourError) {
        const wrapped = new Error(formatSupabaseError(labourError));
        wrapped.cause = labourError;
        throw wrapped;
      }
      workTypeLabour = labourRows || [];
    }
  }

  const locations = (rooms || []).map((room) => ({
    ...room,
    workTypes: features
      .filter((feature) => feature.room_id === room.room_id)
      .map((feature) => ({
        ...feature,
        sections: sections.filter(
          (s) => s.estimate_feature_id === feature.estimate_feature_id,
        ),
        materials: workTypeMaterials.filter(
          (m) => m.estimate_feature_id === feature.estimate_feature_id,
        ),
        labour: workTypeLabour.filter(
          (l) => l.estimate_feature_id === feature.estimate_feature_id,
        ),
      })),
  }));

  return {
    ...estimate,
    locations,
  };
}

/** RG-06 fail-closed message — mirrors invoices.js's "Organization not found." convention. */
export const ESTIMATE_ORGANIZATION_REQUIRED_MESSAGE =
  "Your account isn't linked to an organization yet. Contact your administrator before creating estimates.";

/** Shown only after bounded auto-generated-number retries are exhausted (see createEstimate). */
export const ESTIMATE_NUMBER_RETRY_EXHAUSTED_MESSAGE =
  "Couldn't generate a unique estimate number. Please try again.";

/**
 * Stable diagnostic codes for createEstimate failures. Attached as `.code` on
 * the thrown Error so the UI can show a safe reference alongside the
 * sanitized message (never raw Postgres text) without needing devtools.
 */
export const EST_CREATE_ORG_REQUIRED = "EST_CREATE_ORG_REQUIRED";
export const EST_CREATE_NUMBER_COLLISION_EXHAUSTED = "EST_CREATE_NUMBER_COLLISION_EXHAUSTED";
export const EST_CREATE_HEADER_FAILED = "EST_CREATE_HEADER_FAILED";
export const EST_CREATE_DEFAULT_ROOM_FAILED = "EST_CREATE_DEFAULT_ROOM_FAILED";
export const EST_CREATE_EVENT_FAILED = "EST_CREATE_EVENT_FAILED";

/** Every code createEstimate can attach to a thrown Error's `.code`. */
export const ESTIMATE_CREATE_DIAGNOSTIC_CODES = Object.freeze(
  new Set([
    EST_CREATE_ORG_REQUIRED,
    EST_CREATE_NUMBER_COLLISION_EXHAUSTED,
    EST_CREATE_HEADER_FAILED,
    EST_CREATE_DEFAULT_ROOM_FAILED,
    EST_CREATE_EVENT_FAILED,
  ]),
);

const BOOTSTRAP_STAGE_TO_DIAGNOSTIC_CODE = Object.freeze({
  [ESTIMATE_BOOTSTRAP_STAGE.HEADER]: EST_CREATE_HEADER_FAILED,
  [ESTIMATE_BOOTSTRAP_STAGE.DEFAULT_ROOM]: EST_CREATE_DEFAULT_ROOM_FAILED,
  [ESTIMATE_BOOTSTRAP_STAGE.EVENT]: EST_CREATE_EVENT_FAILED,
});

const MAX_ESTIMATE_NUMBER_ATTEMPTS = 3;

function isUniqueViolation(error) {
  return error?.code === "23505";
}

/**
 * Human-readable labels for common Postgres error codes, for the console log
 * only (never shown to the user, never used to decide behavior — we report
 * whatever code Postgres actually returns rather than guessing).
 */
const PG_ERROR_CODE_LABELS = Object.freeze({
  "23502": "not_null_violation",
  "23503": "foreign_key_violation",
  "23505": "unique_violation",
  "23514": "check_violation",
  "42501": "insufficient_privilege_or_rls_policy",
});

/**
 * Logs full diagnostic detail (Supabase error shape + which bootstrap stage
 * failed + partial-persistence state) to the console. Never logs auth
 * tokens/JWTs/secrets — only the standard Postgrest error fields
 * (code/message/details/hint) and internal record ids, which are not
 * credentials. Runs in every environment (including the Vercel preview,
 * which builds with NODE_ENV=production like any CRA build) — this is the
 * one place in this file diagnostics are intentionally NOT gated by
 * NODE_ENV, since the whole point is to be visible on the preview without
 * desktop devtools.
 */
function logEstimateCreateDiagnostics({
  diagnosticCode,
  error,
  organizationId,
  clientId,
  estimateId,
  defaultRoomId,
}) {
  console.error("[EstimateCreate] diagnostic", {
    diagnosticCode,
    stage: error?.estimateBootstrapStage ?? null,
    supabaseCode: error?.code ?? null,
    supabaseCodeLabel: PG_ERROR_CODE_LABELS[error?.code] ?? null,
    supabaseMessage: error?.message ?? null,
    supabaseDetails: error?.details ?? null,
    supabaseHint: error?.hint ?? null,
    organizationIdPresent: Boolean(organizationId),
    organizationId: organizationId ?? null,
    clientIdPresent: Boolean(clientId),
    clientId: clientId ?? null,
    // Partial-create detection: whichever of these is non-null already
    // persisted before this stage failed (writes are NOT transactional).
    estimateIdReturnedFromHeaderStage:
      estimateId ?? error?.estimateBootstrapContext?.estimateId ?? null,
    defaultRoomIdReturnedFromRoomStage:
      defaultRoomId ?? error?.estimateBootstrapContext?.defaultRoomId ?? null,
  });
}

function attachDiagnosticCode(error, code) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.code = code;
  // Raw Postgres/Supabase error code only (e.g. "23503") — never the message,
  // details, hint, or any SQL/table text. A bare code does not disclose
  // schema shape or policy internals on its own.
  wrapped.dbCode = error?.code ?? null;
  wrapped.cause = error;
  return wrapped;
}

export async function createEstimate(actor, { clientId, estimateNumber, title, userId }) {
  requireEstimateEdit(actor);

  // RG-06 fail-closed: never send organization_id = null to Postgres — every
  // other org-scoped write helper (invoices.js, financeAccounts.js, etc.)
  // already stops here rather than relying on the NOT NULL constraint.
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) {
    const orgError = new Error(ESTIMATE_ORGANIZATION_REQUIRED_MESSAGE);
    orgError.code = EST_CREATE_ORG_REQUIRED;
    logEstimateCreateDiagnostics({
      diagnosticCode: EST_CREATE_ORG_REQUIRED,
      error: null,
      organizationId: null,
      clientId,
    });
    throw orgError;
  }

  if (clientId) {
    let clientQuery = supabase
      .from("clients")
      .select("client_id, archived_at, client_status")
      .eq("client_id", clientId)
      .maybeSingle();
    clientQuery = await scopeClientsQuery(clientQuery, actor);
    const { data: clientRow, error: clientErr } = await clientQuery;
    if (clientErr) throw clientErr;
    if (!clientRow || isClientArchived(clientRow)) {
      throw new Error(OPERATIONAL_LOCK_CLIENT_MESSAGE);
    }
    assertClientOperationallyWritable(clientRow);
  }

  let attemptNumber = estimateNumber;

  for (let attempt = 1; attempt <= MAX_ESTIMATE_NUMBER_ATTEMPTS; attempt += 1) {
    try {
      const { estimateId } = await createDraftEstimate(supabase, {
        organizationId,
        clientId,
        estimateNumber: attemptNumber,
        title,
        createdBy: userId,
        updatedBy: userId,
        eventPayload: { source: "estimate_builder_mvp" },
      });

      return estimateId;
    } catch (error) {
      const stage = error?.estimateBootstrapStage;
      const isHeaderStage = stage === ESTIMATE_BOOTSTRAP_STAGE.HEADER;
      const isLastAttempt = attempt === MAX_ESTIMATE_NUMBER_ATTEMPTS;

      // Only a header-stage unique-violation is retryable (that's the only
      // insert guarded by the estimate-number unique index). A collision at
      // the room/event stage would be a different bug, not a number clash.
      if (isHeaderStage && isUniqueViolation(error) && !isLastAttempt) {
        // Bounded retry: regenerate the number and try again (small, fixed
        // number of attempts — never an unbounded loop).
        attemptNumber = generateEstimateNumber();
        continue;
      }

      if (isHeaderStage && isUniqueViolation(error)) {
        logEstimateCreateDiagnostics({
          diagnosticCode: EST_CREATE_NUMBER_COLLISION_EXHAUSTED,
          error,
          organizationId,
          clientId,
        });
        const exhaustedError = new Error(ESTIMATE_NUMBER_RETRY_EXHAUSTED_MESSAGE);
        exhaustedError.code = EST_CREATE_NUMBER_COLLISION_EXHAUSTED;
        exhaustedError.dbCode = error?.code ?? null;
        throw exhaustedError;
      }

      const diagnosticCode = BOOTSTRAP_STAGE_TO_DIAGNOSTIC_CODE[stage] ?? null;
      logEstimateCreateDiagnostics({
        diagnosticCode,
        error,
        organizationId,
        clientId,
      });

      if (diagnosticCode) {
        throw attachDiagnosticCode(error, diagnosticCode);
      }

      throw error;
    }
  }

  // Unreachable — loop always returns or throws — but keeps control flow explicit.
  throw new Error(ESTIMATE_NUMBER_RETRY_EXHAUSTED_MESSAGE);
}

export async function updateEstimateStatus(actor, estimateId, status, userId) {
  if (status === "approved") {
    requireEstimateApprove(actor);
  } else {
    requireEstimateEdit(actor);
  }

  if (!isValidEstimateStatus(status)) {
    throw new Error(`Invalid estimate status: ${status}`);
  }

  if (status === "converted") {
    throw new Error("Converted status is system-managed.");
  }

  if (status === "archived") {
    throw new Error("Archived status is system-managed.");
  }

  await assertEstimateMutable(estimateId);

  const now = new Date().toISOString();
  const payload = {
    status,
    updated_by: userId,
  };

  if (status === "approved") {
    payload.approved_at = now;
  } else if (status === "declined") {
    payload.rejected_at = now;
  } else if (status === "sent") {
    payload.submitted_at = now;
  }

  const { error } = await supabase
    .from("estimates")
    .update(payload)
    .eq("estimate_id", estimateId);

  if (error) {
    const wrapped = new Error(formatSupabaseError(error));
    wrapped.cause = error;
    throw wrapped;
  }
}

/**
 * Mark an approved estimate as converted after a linked project is created.
 * Sets status, project_id, and conversion audit timestamps (DB requires all three).
 */
export async function markEstimateConverted(actor, estimateId, projectId, userId) {
  requireEstimateConvert(actor);

  if (!estimateId || !projectId) {
    throw new Error("Missing estimate or project id for conversion.");
  }

  const { data: current, error: currentError } = await supabase
    .from("estimates")
    .select("status, project_id")
    .eq("estimate_id", estimateId)
    .single();

  if (currentError) {
    const wrapped = new Error(formatSupabaseError(currentError));
    wrapped.cause = currentError;
    throw wrapped;
  }

  const currentStatus = resolveEstimateStatus(current?.status);
  if (currentStatus === "converted") {
    if (current.project_id === projectId) {
      return;
    }
    throw new Error("This estimate is already converted to another project.");
  }

  if (currentStatus !== "approved") {
    throw new Error("Only approved estimates can be converted.");
  }

  const now = new Date().toISOString();
  const payload = {
    status: "converted",
    project_id: projectId,
    converted_at: now,
    converted_by: userId ?? null,
    updated_by: userId ?? null,
  };

  const { error } = await supabase
    .from("estimates")
    .update(payload)
    .eq("estimate_id", estimateId)
    .is("project_id", null);

  if (error) {
    const wrapped = new Error(formatSupabaseError(error));
    wrapped.cause = error;
    throw wrapped;
  }
}

/** Archive an estimate via the intentional Archive action (not the status dropdown). */
export async function archiveEstimate(actor, estimateId, userId) {
  requireEstimateEdit(actor);

  if (!estimateId) {
    throw new Error("Missing estimate id for archive.");
  }

  const payload = {
    status: "archived",
    updated_by: userId ?? null,
  };

  const { error } = await supabase
    .from("estimates")
    .update(payload)
    .eq("estimate_id", estimateId)
    .neq("status", "archived");

  if (error) {
    const wrapped = new Error(formatSupabaseError(error));
    wrapped.cause = error;
    throw wrapped;
  }
}

export async function updateEstimateHeader(
  actor,
  estimateId,
  { clientId, estimateNumber, title, status, userId },
) {
  await assertEstimateEditable(actor, estimateId);

  const payload = {
    client_id: clientId,
    estimate_number: estimateNumber,
    title,
    updated_by: userId,
  };

  if (status) {
    if (!isValidEstimateStatus(status)) {
      throw new Error(`Invalid estimate status: ${status}`);
    }
    if (status === "converted") {
      throw new Error("Converted status is system-managed.");
    }
    if (status === "archived") {
      throw new Error("Archived status is system-managed.");
    }
    payload.status = status;
  }

  const { error } = await supabase
    .from("estimates")
    .update(payload)
    .eq("estimate_id", estimateId)
    .select("estimate_id");

  if (error) {
    const wrapped = new Error(formatSupabaseError(error));
    wrapped.cause = error;
    throw wrapped;
  }
}

function parseDollarAdjustment(value, label) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return 0;

  const amount = Number(trimmed);
  if (Number.isNaN(amount) || amount < 0) {
    throw new Error(`Enter a valid ${label} amount.`);
  }

  return amount;
}

function parseOptionalFinalEstimatePrice(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;

  const amount = Number(trimmed);
  if (Number.isNaN(amount) || amount < 0) {
    throw new Error("Enter a valid final estimate price.");
  }

  return amount;
}

export async function updateEstimatePricing(
  actor,
  estimateId,
  {
    wasteAdjustment,
    travelAdjustment,
    miscAdjustment,
    finalEstimatePrice,
    taxLines,
    userId,
  },
) {
  await assertEstimateEditable(actor, estimateId);

  const payload = {
    waste_adjustment: parseDollarAdjustment(wasteAdjustment, "waste adjustment"),
    travel_adjustment: parseDollarAdjustment(
      travelAdjustment,
      "travel adjustment",
    ),
    misc_adjustment: parseDollarAdjustment(miscAdjustment, "misc adjustment"),
    final_estimate_price: parseOptionalFinalEstimatePrice(finalEstimatePrice),
    ...normalizeEstimateTaxLinesForSave(taxLines),
    updated_by: userId,
  };

  const { error } = await supabase
    .from("estimates")
    .update(payload)
    .eq("estimate_id", estimateId);

  if (error) {
    const wrapped = new Error(formatSupabaseError(error));
    wrapped.cause = error;
    throw wrapped;
  }
}

async function getWorkTypeRoomId(estimateFeatureId) {
  const { data, error } = await supabase
    .from("estimate_features")
    .select("room_id")
    .eq("estimate_feature_id", estimateFeatureId)
    .single();

  if (error) throw error;
  return data.room_id;
}

/**
 * Write-layer estimate mutability guard.
 *
 * A converted or archived estimate is meant to be a frozen commercial
 * record, but until now every mutation helper below enforced only actor
 * capability (requireEstimateEdit) — never the target estimate's own
 * lifecycle status. UI gating (isEstimateBuilderEditable/isEstimateEditable
 * in EstimateHubScreen.js) already hides the controls, but a manager/admin
 * calling these exported functions directly (stale component state, a
 * race after conversion completes while a builder screen is still mounted,
 * a script, or direct API use) could still mutate a converted estimate's
 * structure/pricing with no rejection from the write layer itself.
 *
 * assertEstimateMutable() closes that gap by always re-loading the
 * estimate's current status from the database (never trusting a status the
 * caller already had in memory) and failing closed for both
 * ESTIMATE_SYSTEM_MANAGED_STATUSES values (converted, archived) — the same
 * set the status dropdown already excludes, applied here for content
 * mutations too, for consistency. Every content-mutation helper below
 * (header, locations, features, measurements, planned materials, planned
 * labour, pricing/tax) routes through one of the assertEstimateEditable*
 * wrappers instead of calling requireEstimateEdit directly, so lifecycle
 * enforcement cannot be bypassed by a caller that only checks capability.
 *
 * This intentionally does NOT touch the two functions whose entire job is
 * to legitimately transition an estimate INTO one of these frozen states:
 * markEstimateConverted() (approved -> converted, called once by the
 * Estimate -> Project conversion flow) and archiveEstimate() (an
 * intentional, always-available lifecycle action, including from
 * "converted" — see archiveEstimate for why). updateEstimateStatus() (the
 * general-purpose status dropdown setter) additionally calls
 * assertEstimateMutable() itself, since — unlike the two functions above —
 * it has no legitimate reason to run at all once an estimate is already
 * converted or archived.
 */
async function assertEstimateMutable(estimateId) {
  if (!estimateId) {
    throw new Error("Missing estimate id.");
  }

  const { data, error } = await supabase
    .from("estimates")
    .select("status")
    .eq("estimate_id", estimateId)
    .single();

  if (error) {
    const wrapped = new Error(formatSupabaseError(error));
    wrapped.cause = error;
    throw wrapped;
  }

  const status = resolveEstimateStatus(data?.status);
  if (ESTIMATE_SYSTEM_MANAGED_STATUSES.has(status)) {
    throw new Error(
      `This estimate is ${formatEstimateStatus(status).toLowerCase()} and can no longer be edited.`,
    );
  }
}

/** Capability + lifecycle-state guard for functions that already hold estimateId. */
async function assertEstimateEditable(actor, estimateId) {
  requireEstimateEdit(actor);
  await assertEstimateMutable(estimateId);
}

async function getEstimateIdForRoom(roomId) {
  const { data, error } = await supabase
    .from("estimate_rooms")
    .select("estimate_id")
    .eq("room_id", roomId)
    .single();

  if (error) throw error;
  return data.estimate_id;
}

async function getEstimateIdForFeature(estimateFeatureId) {
  const roomId = await getWorkTypeRoomId(estimateFeatureId);
  return getEstimateIdForRoom(roomId);
}

async function getFeatureIdForSection(sectionId) {
  const { data, error } = await supabase
    .from("estimate_room_sections")
    .select("estimate_feature_id")
    .eq("section_id", sectionId)
    .single();

  if (error) throw error;
  return data.estimate_feature_id;
}

async function getFeatureIdForMaterial(roomMaterialId) {
  const { data, error } = await supabase
    .from("estimate_room_materials")
    .select("estimate_feature_id")
    .eq("room_material_id", roomMaterialId)
    .single();

  if (error) throw error;
  return data.estimate_feature_id;
}

async function getFeatureIdForLabour(labourId) {
  const { data, error } = await supabase
    .from("estimate_room_labour")
    .select("estimate_feature_id")
    .eq("labour_id", labourId)
    .single();

  if (error) throw error;
  return data.estimate_feature_id;
}

/** Capability + lifecycle-state guard for functions keyed by Location (room) id. */
async function assertEstimateEditableForRoom(actor, roomId) {
  requireEstimateEdit(actor);
  await assertEstimateMutable(await getEstimateIdForRoom(roomId));
}

/** Capability + lifecycle-state guard for functions keyed by Feature id. */
async function assertEstimateEditableForFeature(actor, estimateFeatureId) {
  requireEstimateEdit(actor);
  await assertEstimateMutable(await getEstimateIdForFeature(estimateFeatureId));
}

/** Capability + lifecycle-state guard for functions keyed by Measurement (section) id. */
async function assertEstimateEditableForSection(actor, sectionId) {
  requireEstimateEdit(actor);
  const featureId = await getFeatureIdForSection(sectionId);
  await assertEstimateMutable(await getEstimateIdForFeature(featureId));
}

/** Capability + lifecycle-state guard for functions keyed by planned Material id. */
async function assertEstimateEditableForMaterial(actor, roomMaterialId) {
  requireEstimateEdit(actor);
  const featureId = await getFeatureIdForMaterial(roomMaterialId);
  await assertEstimateMutable(await getEstimateIdForFeature(featureId));
}

/** Capability + lifecycle-state guard for functions keyed by planned Labour id. */
async function assertEstimateEditableForLabour(actor, labourId) {
  requireEstimateEdit(actor);
  const featureId = await getFeatureIdForLabour(labourId);
  await assertEstimateMutable(await getEstimateIdForFeature(featureId));
}

export async function addEstimateLocation(actor, estimateId, name, sortOrder) {
  await assertEstimateEditable(actor, estimateId);

  const { data, error } = await supabase
    .from("estimate_rooms")
    .insert({
      estimate_id: estimateId,
      name,
      sort_order: sortOrder,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateEstimateLocation(actor, roomId, { name, roomType }) {
  await assertEstimateEditableForRoom(actor, roomId);

  const { error } = await supabase
    .from("estimate_rooms")
    .update({
      name,
      room_type: roomType || null,
    })
    .eq("room_id", roomId);

  if (error) throw error;
}

export async function deleteEstimateLocation(actor, roomId) {
  await assertEstimateEditableForRoom(actor, roomId);

  const { error } = await supabase
    .from("estimate_rooms")
    .update({ is_active: false })
    .eq("room_id", roomId);

  if (error) throw error;
}

export async function nextWorkTypeSortOrder(roomId) {
  const { data: siblings, error: siblingsError } = await supabase
    .from("estimate_features")
    .select("sort_order")
    .eq("room_id", roomId)
    .eq("is_active", true)
    .order("sort_order", { ascending: false })
    .limit(1);

  if (siblingsError) throw siblingsError;

  return siblings?.[0]?.sort_order != null ? siblings[0].sort_order + 1 : 0;
}

export async function addEstimateWorkType(actor, roomId, featureTypeId) {
  await assertEstimateEditableForRoom(actor, roomId);

  const { data: featureType, error: featureTypeError } = await supabase
    .from("feature_types")
    .select("*")
    .eq("feature_type_id", featureTypeId)
    .single();

  if (featureTypeError) throw featureTypeError;

  const sortOrder = await nextWorkTypeSortOrder(roomId);

  const { data, error } = await supabase
    .from("estimate_features")
    .insert({
      room_id: roomId,
      feature_type_id: featureTypeId,
      feature_type_name_snapshot: featureType.name,
      sort_order: sortOrder,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function addCustomEstimateWorkType(actor, roomId, name) {
  await assertEstimateEditableForRoom(actor, roomId);

  const trimmed = name.trim();
  if (!trimmed) {
    throw new Error("Work type name is required.");
  }

  const sortOrder = await nextWorkTypeSortOrder(roomId);

  const { data, error } = await supabase
    .from("estimate_features")
    .insert({
      room_id: roomId,
      name_override: trimmed,
      sort_order: sortOrder,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateEstimateWorkTypeName(actor, estimateFeatureId, name) {
  await assertEstimateEditableForFeature(actor, estimateFeatureId);

  const trimmed = name.trim();
  if (!trimmed) {
    throw new Error("Work type name is required.");
  }

  const { error } = await supabase
    .from("estimate_features")
    .update({ name_override: trimmed })
    .eq("estimate_feature_id", estimateFeatureId);

  if (error) throw error;
}

export async function updateEstimateWorkTypeAssembly(
  actor,
  estimateFeatureId,
  assemblyTemplateId,
) {
  await assertEstimateEditableForFeature(actor, estimateFeatureId);

  if (!assemblyTemplateId) {
    const { error } = await supabase
      .from("estimate_features")
      .update({
        assembly_template_id: null,
        assembly_name_snapshot: null,
        assembly_kind_snapshot: null,
        assembly_template_version_snapshot: null,
      })
      .eq("estimate_feature_id", estimateFeatureId);

    if (error) throw error;
    return;
  }

  const { data: assembly, error: assemblyError } = await supabase
    .from("assembly_templates")
    .select("*")
    .eq("assembly_template_id", assemblyTemplateId)
    .single();

  if (assemblyError) throw assemblyError;

  const { data: feature, error: featureError } = await supabase
    .from("estimate_features")
    .select("feature_type_id")
    .eq("estimate_feature_id", estimateFeatureId)
    .single();

  if (featureError) throw featureError;

  if (feature.feature_type_id !== assembly.feature_type_id) {
    throw new Error("Assembly does not match the selected work type.");
  }

  const { error } = await supabase
    .from("estimate_features")
    .update({
      assembly_template_id: assemblyTemplateId,
      assembly_name_snapshot: assembly.name,
      assembly_kind_snapshot: assembly.assembly_kind,
      assembly_template_version_snapshot: assembly.version,
    })
    .eq("estimate_feature_id", estimateFeatureId);

  if (error) throw error;
}

export async function deleteEstimateWorkType(actor, estimateFeatureId) {
  await assertEstimateEditableForFeature(actor, estimateFeatureId);

  const { error } = await supabase
    .from("estimate_features")
    .update({ is_active: false })
    .eq("estimate_feature_id", estimateFeatureId);

  if (error) throw error;
}

export async function addMeasurement(actor, estimateFeatureId, section) {
  await assertEstimateEditableForFeature(actor, estimateFeatureId);

  const roomId = await getWorkTypeRoomId(estimateFeatureId);

  const { data: siblings, error: siblingsError } = await supabase
    .from("estimate_room_sections")
    .select("sort_order")
    .eq("estimate_feature_id", estimateFeatureId)
    .eq("is_active", true)
    .order("sort_order", { ascending: false })
    .limit(1);

  if (siblingsError) throw siblingsError;

  const sortOrder =
    siblings?.[0]?.sort_order != null ? siblings[0].sort_order + 1 : 0;

  const payload = {
    room_id: roomId,
    estimate_feature_id: estimateFeatureId,
    sort_order: sortOrder,
    ...buildMeasurementWritePayload(section),
  };

  const { data, error } = await supabase
    .from("estimate_room_sections")
    .insert(payload)
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function updateMeasurement(actor, sectionId, section) {
  await assertEstimateEditableForSection(actor, sectionId);

  const payload = buildMeasurementWritePayload(section);

  const { error } = await supabase
    .from("estimate_room_sections")
    .update(payload)
    .eq("section_id", sectionId);

  if (error) throw error;
}

export async function deleteMeasurement(actor, sectionId) {
  await assertEstimateEditableForSection(actor, sectionId);

  const { error } = await supabase
    .from("estimate_room_sections")
    .update({ is_active: false })
    .eq("section_id", sectionId);

  if (error) throw error;
}

function parseOptionalPositiveNumber(value, fieldLabel) {
  if (value === "" || value == null) return null;

  const parsed = Number(value);
  if (!parsed || parsed <= 0) {
    throw new Error(`${fieldLabel} must be greater than zero.`);
  }

  return parsed;
}

function parseOptionalNonNegativeNumber(value, fieldLabel) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed) || parsed < 0) {
    throw new Error(`Enter a valid ${fieldLabel}.`);
  }

  return parsed;
}

export async function addWorkTypeMaterial(
  actor,
  estimateFeatureId,
  { material, customProduct, quantity, notes },
) {
  await assertEstimateEditableForFeature(actor, estimateFeatureId);

  const roomId = await getWorkTypeRoomId(estimateFeatureId);

  const { data: siblings, error: siblingsError } = await supabase
    .from("estimate_room_materials")
    .select("sort_order")
    .eq("estimate_feature_id", estimateFeatureId)
    .eq("is_active", true)
    .order("sort_order", { ascending: false })
    .limit(1);

  if (siblingsError) throw siblingsError;

  const sortOrder = (siblings?.[0]?.sort_order ?? -1) + 1;
  const parsedQuantity = parseOptionalPositiveNumber(quantity, "Quantity");

  let payload;

  if (customProduct) {
    const productName = customProduct.productName?.trim();
    if (!productName) {
      throw new Error("Enter a product name.");
    }

    payload = {
      room_id: roomId,
      estimate_feature_id: estimateFeatureId,
      material_id: null,
      quantity: parsedQuantity,
      material_name_snapshot: productName,
      sku_snapshot: null,
      unit_of_measure_snapshot: customProduct.unitType?.trim() || null,
      unit_cost_snapshot: Number(customProduct.unitCost) || 0,
      sell_price_snapshot: parseOptionalNonNegativeNumber(
        customProduct.sellPrice,
        "sell price",
      ),
      notes: notes?.trim() || null,
      sort_order: sortOrder,
    };
  } else {
    if (!material?.material_id) {
      throw new Error("Select a product.");
    }

    payload = {
      room_id: roomId,
      estimate_feature_id: estimateFeatureId,
      material_id: material.material_id,
      quantity: parsedQuantity,
      material_name_snapshot: material.material_name,
      sku_snapshot: material.sku || null,
      unit_of_measure_snapshot: material.unit_of_measure || null,
      unit_cost_snapshot: Number(material.unit_cost) || 0,
      sell_price_snapshot:
        material.sell_price == null || material.sell_price === ""
          ? null
          : Number(material.sell_price),
      notes: notes?.trim() || null,
      sort_order: sortOrder,
    };
  }

  const { error } = await supabase.from("estimate_room_materials").insert(payload);

  if (error) throw error;
}

export async function updateWorkTypeMaterial(actor, roomMaterialId, { quantity, notes }) {
  await assertEstimateEditableForMaterial(actor, roomMaterialId);

  const payload = {
    notes: notes?.trim() || null,
  };

  if (quantity === "" || quantity == null) {
    payload.quantity = null;
  } else {
    const parsedQuantity = Number(quantity);
    if (!parsedQuantity || parsedQuantity <= 0) {
      throw new Error("Quantity must be greater than zero.");
    }
    payload.quantity = parsedQuantity;
  }

  const { error } = await supabase
    .from("estimate_room_materials")
    .update(payload)
    .eq("room_material_id", roomMaterialId);

  if (error) throw error;
}

export async function deleteWorkTypeMaterial(actor, roomMaterialId) {
  await assertEstimateEditableForMaterial(actor, roomMaterialId);

  const { error } = await supabase
    .from("estimate_room_materials")
    .update({ is_active: false })
    .eq("room_material_id", roomMaterialId);

  if (error) throw error;
}

function parseOptionalHourlyRate(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;

  const rate = Number(trimmed);
  if (Number.isNaN(rate) || rate < 0) {
    throw new Error("Enter a valid rate, or leave blank.");
  }

  return rate;
}

function normalizeLabourPricingMethod(value) {
  if (value === LABOUR_PRICING_METHOD.SQUARE_FOOT) {
    return LABOUR_PRICING_METHOD.SQUARE_FOOT;
  }
  return LABOUR_PRICING_METHOD.HOURLY;
}

function buildLabourRowPayload({
  labourActivity,
  estimatedHours,
  hourlyRate,
  notes,
  pricingMethod,
}) {
  const method = normalizeLabourPricingMethod(pricingMethod);
  const activity = labourActivity?.trim();
  if (!activity) {
    throw new Error("Enter a labour activity.");
  }

  let hours = null;
  if (method === LABOUR_PRICING_METHOD.HOURLY) {
    hours = Number(estimatedHours);
    if (!hours || hours <= 0) {
      throw new Error("Enter estimated hours greater than zero.");
    }
  }

  return {
    labour_activity: activity,
    pricing_method: method,
    estimated_hours: hours,
    hourly_rate: parseOptionalHourlyRate(hourlyRate),
    notes: notes?.trim() || null,
  };
}

export async function addWorkTypeLabour(
  actor,
  estimateFeatureId,
  { labourActivity, estimatedHours, hourlyRate, notes, pricingMethod },
) {
  await assertEstimateEditableForFeature(actor, estimateFeatureId);

  const roomId = await getWorkTypeRoomId(estimateFeatureId);
  const payload = buildLabourRowPayload({
    labourActivity,
    estimatedHours,
    hourlyRate,
    notes,
    pricingMethod,
  });

  const { error } = await supabase.from("estimate_room_labour").insert({
    room_id: roomId,
    estimate_feature_id: estimateFeatureId,
    ...payload,
  });

  if (error) throw error;
}

export async function updateWorkTypeLabour(
  actor,
  labourId,
  { labourActivity, estimatedHours, hourlyRate, notes, pricingMethod },
) {
  await assertEstimateEditableForLabour(actor, labourId);

  const payload = buildLabourRowPayload({
    labourActivity,
    estimatedHours,
    hourlyRate,
    notes,
    pricingMethod,
  });

  const { error } = await supabase
    .from("estimate_room_labour")
    .update(payload)
    .eq("labour_id", labourId);

  if (error) throw error;
}

export async function deleteWorkTypeLabour(actor, labourId) {
  await assertEstimateEditableForLabour(actor, labourId);

  const { error } = await supabase
    .from("estimate_room_labour")
    .delete()
    .eq("labour_id", labourId);

  if (error) throw error;
}

export function formatLocationLabel(location) {
  if (!location) return "Location";
  if (location.is_default_scope) {
    return location.name || "Job-Wide / General";
  }
  return location.name || "Untitled Location";
}

export function workTypeLabel(workType) {
  const override = workType?.name_override?.trim();
  if (override) return override;

  return (
    workType?.feature_types?.name ||
    workType?.feature_type_name_snapshot ||
    "Work Type"
  );
}

export function isEstimateEditable(status) {
  return status === "draft";
}
